// One live presence subscription for the whole app (Brief 4) — the SINGLE
// presence/heartbeat owner. Runs `usePresence` ONCE
// and `useFocusHeartbeat` ONCE and shares the result, so the shell badge, the
// tab title, the sidebar's per-project counts, and every session/channel view
// read ONE presence stream and post ONE focus heartbeat — not many. Crucially it
// makes NOTICE reach you ANYWHERE: the tab title carries the needs-me count, so a
// backgrounded tab or any non-session surface still signals "N need you" — the
// Rest promise (you can put it down and it will find you).
//
// SINGLE-OWNER GUARD: each app mounts EXACTLY ONE <PresenceProvider> near its
// root. Mounting two (in the same process) would post the focus heartbeat and
// drive the needs-me title from two sources — exactly the drift the single-owner rule forbids.
// A module-level mount counter makes that a loud failure in dev/test instead of
// a silent double-signal. In two SEPARATE app processes (web tab + desktop
// window) each has its own one owner — that is by design, not a violation.

import {
  createContext,
  useContext,
  useEffect,
  type ReactNode,
} from "react";
import type { SessionPresence } from "@waytide/core";
import { usePresence } from "./usePresence";
import { useFocusHeartbeat } from "./useFocusHeartbeat";
import { isNeedsInput } from "../model/presenceModel";

interface PresenceState {
  sessions: SessionPresence[];
  loaded: boolean;
  error: string | null;
  needsCount: number;
  /** Re-read `GET /presence` (server truth) — called after a session is
   * assigned to a project so the sidebar + sessions regroup at once. */
  refresh: () => void;
}

const PresenceContext = createContext<PresenceState | null>(null);

const BASE_TITLE = "Waytide";

// The number of <PresenceProvider> instances mounted in THIS process. Must never
// exceed 1 — the presence/heartbeat owner is singular by contract. Exported so a
// test can assert the guard catches a second mount.
let MOUNTED_OWNERS = 0;

/** Test seam: read the live owner count (asserts "one source" honestly). */
export function presenceOwnerCount(): number {
  return MOUNTED_OWNERS;
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  // Guard: a second concurrent owner means needs-me + the heartbeat would fire
  // from two sources. Surface it loudly (dev/test) — never silently double-post.
  useEffect(() => {
    MOUNTED_OWNERS += 1;
    if (MOUNTED_OWNERS > 1) {
      console.error(
        "Waytide: more than one <PresenceProvider> is mounted in this app — " +
          "the presence/heartbeat owner must be singular. The " +
          "needs-me signal and the focus heartbeat must not fire from two " +
          "sources. Mount exactly one provider at the app root.",
      );
    }
    return () => {
      MOUNTED_OWNERS -= 1;
    };
  }, []);

  const { sessions, loaded, error, refresh } = usePresence();
  const needsCount = sessions.filter((s) => isNeedsInput(s.status)).length;

  // Brief 5 pt16: report THIS user's focus (active/backgrounded/idle) once for
  // the whole signed-in session, so the backend knows if they're reachable
  // in-app and the async-steering escalation can wait-in-thread vs. reach-async.
  // This is the ONLY caller of useFocusHeartbeat in @waytide/ui — guarded by the
  // single-owner test so no surface (e.g. ChannelsPage) re-adds a second beat.
  useFocusHeartbeat();

  // The tab is a Notice channel: a backgrounded or non-session surface still
  // carries the one number that matters. Reset to the calm title on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.title =
      needsCount > 0 ? `(${needsCount}) ${BASE_TITLE} — needs you` : BASE_TITLE;
    return () => {
      document.title = BASE_TITLE;
    };
  }, [needsCount]);

  return (
    <PresenceContext.Provider
      value={{ sessions, loaded, error, needsCount, refresh }}
    >
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresenceContext(): PresenceState {
  const v = useContext(PresenceContext);
  if (!v) {
    throw new Error("usePresenceContext must be used within <PresenceProvider>");
  }
  return v;
}
